// Bump this only when the terms themselves change. It must be a fixed date, not
// generated at render time — a customer who agrees to these terms needs a version
// we can point back to later.
const EFFECTIVE_DATE = "August 13, 2026";

export default function TermsOfService() {
  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-sm mt-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms of Service</h1>

      <div className="space-y-6 text-gray-700 leading-relaxed">
        <p><strong>Effective Date:</strong> {EFFECTIVE_DATE}</p>
        
        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
          <p>
            By accessing and using FreshOrder ("the Service"), you accept and agree to be bound by these Terms of Service. 
            If you do not agree to these terms, you may not use the Service.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
          <p>
            FreshOrder is an online platform that enables customers to place orders and provides 
            business management tools including:
          </p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Order placement and management system</li>
            <li>Customer account management</li>
            <li>Product catalog and ordering interface</li>
            <li>QuickBooks integration for invoice management</li>
            <li>Order tracking and reporting tools</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>
          <p>
            To use certain features of the Service, you must create an account. You are responsible for:
          </p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Providing accurate and complete information when creating your account</li>
            <li>Maintaining the security of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Notifying us immediately of any unauthorized use of your account</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Orders and Payments</h2>
          <p>
            When placing orders through the Service:
          </p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>All orders are subject to acceptance and availability</li>
            <li>Prices are subject to change without notice</li>
            <li>Payment terms will be communicated separately</li>
            <li>Order modifications may be subject to availability and timing constraints</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. QuickBooks Integration</h2>
          <p>
            If you choose to connect your QuickBooks account:
          </p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>You authorize us to access your QuickBooks data as specified during the connection process</li>
            <li>We may create invoice drafts in your QuickBooks account based on your orders</li>
            <li>You can disconnect the integration at any time through the Settings page</li>
            <li>You remain responsible for reviewing and managing invoices in your QuickBooks account</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Use the Service for any unlawful purpose or in violation of these Terms</li>
            <li>Attempt to gain unauthorized access to the Service or other user accounts</li>
            <li>Interfere with or disrupt the Service or servers connected to the Service</li>
            <li>Use the Service to transmit harmful, offensive, or inappropriate content</li>
            <li>Attempt to reverse engineer or copy any features or functionality of the Service</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Intellectual Property</h2>
          <p>
            The Service and its original content, features, and functionality are and will remain the exclusive 
            property of FreshOrder and its licensors. The Service is protected by copyright, trademark, and other laws.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Privacy</h2>
          <p>
            Your privacy is important to us. Please review our Privacy Policy, which also governs your use 
            of the Service, to understand our practices.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Disclaimers</h2>
          <p>
            The Service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, either express or implied. 
            We do not warrant that the Service will be uninterrupted, secure, or free of errors.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Limitation of Liability</h2>
          <p>
            In no event shall FreshOrder be liable for any indirect, incidental, special, consequential, or punitive damages,
            including without limitation, loss of profits, data, use, goodwill, or other intangible losses.
          </p>
          <p className="mt-4">
            To the maximum extent permitted by law, FreshOrder's total aggregate liability arising out of or relating
            to the Service, whether in contract, tort, or otherwise, shall not exceed the total fees you paid to
            FreshOrder in the twelve (12) months immediately preceding the event giving rise to the claim. Where no
            fees have been paid, FreshOrder's total aggregate liability shall not exceed one hundred Canadian dollars
            (CAD $100).
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Termination</h2>
          <p>
            We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, 
            for any reason, including if you breach these Terms.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. We will notify users of any material changes 
            by posting the new Terms on this page with an updated effective date.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Information</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us at:
          </p>
          <div className="mt-2">
            <p><strong>FreshOrder</strong></p>
            {/* support@freshorder.io is the intended address but the mailbox does not exist yet.
                Point at hello@ until it does. Switch back once support@ is live. */}
            <p>Email: hello@freshorder.io</p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the Province of Ontario
            and the federal laws of Canada applicable therein, without regard to conflict of law provisions. You
            and FreshOrder agree to the exclusive jurisdiction of the courts of the Province of Ontario for any
            dispute arising out of or relating to these Terms or the Service.
          </p>
        </section>
      </div>
    </div>
  );
}
